import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TabSelector } from "./TabSelector";
import DataTable from "react-data-table-component";
import TooltipBox from "./Tooltip";
import DropdownFilter from "./DropdownFilter";
import ButtonIconH from "./ButtonIconH";
import "../css/DocumentManagement.css";
import { dashboardDMColumns } from "../js/tableColumns";
import { userData, LIMIT, formatValueUnit } from "../js/utils";
import { getSectionName, mimeTypes } from "../js/fileManagement";
import { useDashboardFiles } from "../hooks/useDashboardFiles";
import { post } from "../js/post";
import FileDisplay from "./FileDisplay";
import ActionPopup from "./ActionPopup";
import { SendFile } from "./SendFile";

export function DashboardDocumentManagement({
  createPopupQuestion,
  removePopupQuestion,
}) {
  const [selectedPanelView, setSelectedPanelView] = useState("Ventas");
  const [start, setStart] = useState(0);
  const [limit, setLimit] = useState(LIMIT);
  const [data, refreshData] = useDashboardFiles(
    getSectionName(selectedPanelView),
    start,
    limit,
  );
  const [fileList, setFileList] = useState([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const appendMode = useRef(false);
  const [actionComponent, setActionComponent] = useState(null);

  // console.log("fileList: ", fileList);
  // console.log("selectedPanelView: ", selectedPanelView);

  const getFile = useCallback(async (id, nombre, tipoArchivo, open = false) => {
    const response = await post(
      "support-extra/gestionDocumental/ObtenerFicheroGD.php",
      {
        token: userData.token,
        idFichero: id,
      },
    );

    const mime = mimeTypes[tipoArchivo] || "application/octet-stream";

    if (open) {
      const dataUrl = response.fichero.startsWith("data:")
        ? response.fichero
        : `data:${mime};base64,${response.fichero}`;
      setActionComponent({
        name: "Visor de fichero",
        component: (
          <div className="container-panel file-info">
            <FileDisplay base64Data={dataUrl} />
          </div>
        ),
        fullscreen: true,
      });
    } else {
      const rawBase64 = response.fichero.startsWith("data:")
        ? response.fichero.split(",")[1]
        : response.fichero;
      const byteChars = atob(rawBase64);
      const byteArray = new Uint8Array(
        [...byteChars].map((c) => c.charCodeAt(0)),
      );
      const blob = new Blob([byteArray], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nombre;
      a.click();
      URL.revokeObjectURL(url);
    }
  }, []);

  const deleteFile = useCallback(async (id) => {
    await post("support-extra/gestionDocumental/EliminarFicheroGD.php", {
      token: userData.token,
      idFichero: id,
    });
  }, []);

  const updatedColumns = useMemo(
    () =>
      dashboardDMColumns.map((col) => {
        if (col.name === "") {
          return {
            ...col,
            selector: (row) => (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <button
                  onClick={async () => {
                    await getFile(
                      row.idFichero,
                      row.nombre,
                      row.tipoArchivo,
                      true,
                    );
                  }}
                >
                  <img
                    src="/images/open-file-blue.svg"
                    className="icon-pref"
                    data-tooltip-id={"open" + String(row.idFichero)}
                    alt="ver"
                  ></img>
                  <TooltipBox
                    id={"open" + String(row.idFichero)}
                    content={
                      <div className="opt-table-tooltip grid-5">
                        <div className="cell combined-cells-5 cell-start-content">
                          Abrir
                        </div>
                      </div>
                    }
                  ></TooltipBox>
                </button>
                <button
                  onClick={async () => {
                    await getFile(row.idFichero, row.nombre, row.tipoArchivo);
                  }}
                >
                  <img
                    src="/images/download-blue.svg"
                    className="icon-pref"
                    data-tooltip-id={"download" + String(row.idFichero)}
                    alt="descargar"
                  ></img>
                  <TooltipBox
                    id={"download" + String(row.idFichero)}
                    content={
                      <div className="opt-table-tooltip grid-5">
                        <div className="cell combined-cells-5 cell-start-content">
                          Descargar
                        </div>
                      </div>
                    }
                  ></TooltipBox>
                </button>
                <button
                  onClick={() => {
                    setActionComponent({
                      name: "Envío de fichero",
                      component: (
                        <SendFile
                          id={row.idFichero}
                          createPopupQuestion={createPopupQuestion}
                          removePopupQuestion={removePopupQuestion}
                        />
                      ),
                      fullscreen: false,
                    });
                  }}
                >
                  <img
                    src="/images/send-blue.svg"
                    className="icon-pref"
                    data-tooltip-id={"send" + String(row.idFichero)}
                    alt="enviar"
                  ></img>
                  <TooltipBox
                    id={"send" + String(row.idFichero)}
                    content={
                      <div className="opt-table-tooltip grid-5">
                        <div className="cell combined-cells-5 cell-start-content">
                          Enviar
                        </div>
                      </div>
                    }
                  ></TooltipBox>
                </button>
                <button
                  onClick={() => {
                    createPopupQuestion(
                      `¿Desea eliminar el fichero ${row.nombre}?`,
                      async () => {
                        await deleteFile(row.idFichero);
                        setStart(0);
                        refreshData();
                        removePopupQuestion();
                      },
                      removePopupQuestion,
                      "Sí",
                      "No",
                    );
                  }}
                >
                  <img
                    src="/images/remove-blue.svg"
                    className="icon-pref"
                    data-tooltip-id={"delete" + String(row.idFichero)}
                    alt="eliminar"
                  ></img>
                  <TooltipBox
                    id={"delete" + String(row.idFichero)}
                    content={
                      <div className="opt-table-tooltip grid-5">
                        <div className="cell combined-cells-5 cell-start-content">
                          Eliminar
                        </div>
                      </div>
                    }
                  ></TooltipBox>
                </button>
              </div>
            ),
          };
        }

        return col;
      }),
    [getFile, deleteFile, refreshData, createPopupQuestion, removePopupQuestion],
  );

  useEffect(() => {
    if (appendMode.current) {
      setFileList((prev) => [...prev, ...data.ficheros]);
      appendMode.current = false;
    } else {
      setFileList(data.ficheros);
    }
    setTotalFiles(data.total);
  }, [data]);

  useEffect(() => {
    setStart(0);
    setLimit(LIMIT);
  }, [selectedPanelView]);

  return (
    <>
      {actionComponent !== null && (
        <ActionPopup
          name={actionComponent.name}
          children={actionComponent.component}
          fullscreen={actionComponent.fullscreen}
          onClose={() => {
            setActionComponent(null);
          }}
        ></ActionPopup>
      )}
      <div className="dashboard-dm-container">
        <TabSelector
          options={["Ventas", "Almacén", "Clientes"]}
          style={{ borderBottom: "none" }}
          selected={selectedPanelView}
          onChange={(e) => {
            setSelectedPanelView(e);
          }}
        />
        <div className="thin-section-rounded-border dashboard-dm-files-container">
          <div className="dashboard-dm-header">
            <div
              className="opt-table-title opt-table-title-button"
              style={{ marginBottom: 0 }}
            >
              <div>
                Listado de ficheros ({formatValueUnit(fileList.length)} de{" "}
                <span style={{ color: "#41b3ff" }}>
                  {formatValueUnit(totalFiles)}
                </span>
                )
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 30,
                }}
              >
                <DropdownFilter
                  optionList={[
                    { id: "00", text: "25", active: limit === 25 },
                    { id: "01", text: "50", active: limit === 50 },
                    { id: "02", text: "100", active: limit === 100 },
                  ]}
                  op={"Mostrando " + limit}
                  selectedOptions={[]}
                  onChange={(e) => {
                    setLimit(parseInt(e.text));
                    setStart(0);
                  }}
                ></DropdownFilter>
              </div>
            </div>
          </div>
          <DataTable
            columns={updatedColumns}
            data={fileList}
            className="custom-datatable dashboard-custom-table"
            noDataComponent="No hay datos disponibles."
          />
          <ButtonIconH
            text="Ver más"
            small={true}
            onClick={() => {
              if (fileList.length < totalFiles) {
                appendMode.current = true;
                setStart(fileList.length);
              }
            }}
          ></ButtonIconH>
        </div>
      </div>
    </>
  );
}
